import { IconAlertTriangle, IconCheck, IconFile, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useSubirDocumento } from "@/features/documentos/hooks/use-documentos-mutations";
import { Alert, Badge, Button, Card, DatePickerInput, Group, IconAction, Select, Stack, Text, ThemeIcon } from "@/components/ui";
import { formatIso, hoyNegocio, parseIso } from "@/features/datos-generales/utils/iso-date";
import { useLimitesDocumentos } from "@/features/documentos/hooks/use-limites-documentos";
import { documentosApi, type TarjetaRequisito } from "@/features/documentos/services/documentos.api";
import { toastErrorApi } from "@/lib/toast";
import type { DocumentoVinculado } from "@/lib/types";
import type { RevisionResumen } from "@/features/revision-documental/services/revision-documental.api";
import { CategoriaDropzone, type CategoriaDropzoneEstado } from "./CategoriaDropzone";
import { DocumentoRow } from "./DocumentoRow";

interface Props {
  tarjeta: TarjetaRequisito;
  documentos: DocumentoVinculado[];
  solicitudId: string;
  puedeAdjuntar: boolean;
  /** Gestión Documental · fase 3 · revisión de cada documento (por documentoId). */
  revisiones?: ReadonlyMap<string, RevisionResumen>;
}

/**
 * Archivo que espera su tipo de documento (P31): uno suelto (local, aún no subido) o uno
 * extraído de un ZIP por el servidor (ya está en su área temporal).
 */
interface ArchivoPorClasificar {
  id: string;
  nombre: string;
  file?: File;
  deZip?: { loteId: string; archivoId: string };
  tipo: string | null;
  /** Fase 4 · fecha de emisión (yyyy-MM-dd) · se pide si el tipo elegido maneja vigencia. */
  fechaEmision: string | null;
  subiendo: boolean;
}

/** «mínimo 1 · máximo 1» · «al menos 1» · «opcional · hasta 3»… */
export function textoCantidad(t: Pick<TarjetaRequisito, "minimo" | "maximo">): string {
  if (t.maximo === t.minimo && t.minimo > 0) return t.minimo === 1 ? "exactamente 1" : `exactamente ${t.minimo}`;
  const min = t.minimo > 0 ? `al menos ${t.minimo}` : "opcional";
  return t.maximo ? `${min} · hasta ${t.maximo}` : min;
}

/**
 * Gestión Documental (fase 2) · tarjeta de un REQUISITO del expediente (sustituye a la tarjeta por
 * categoría fija): cabecera con su estado, dropzone (oculto al llegar al máximo) y la lista de sus
 * documentos. Si la categoría se clasifica con tipo y el requisito no lo fija, cada archivo queda
 * «por clasificar» con su Select (P31); en «Adicional» genérico acepta además .zip (el servidor lo
 * descomprime y valida).
 */
export function RequisitoCard({ tarjeta, documentos, solicitudId, puedeAdjuntar, revisiones }: Props) {
  const etiqueta = tarjeta.tipoDocumento ? `${tarjeta.categoriaNombre} · ${tarjeta.tipoDocumento}` : tarjeta.categoriaNombre;
  const subir = useSubirDocumento(solicitudId, etiqueta);

  const numeroCfdi = tarjeta.numeroCfdi ?? 1;
  // Se elige tipo cuando la categoría lo exige y el requisito no lo fija (o hay tipos opcionales).
  const eligeTipo = !tarjeta.tipoDocumento && (tarjeta.requiereTipo || tarjeta.tiposPermitidos.length > 0);
  const aceptaZip = tarjeta.categoria === "Adicional" && !tarjeta.tipoDocumento;
  const accept = [...tarjeta.extensiones, ...(aceptaZip ? [".zip"] : [])].join(",");
  const unSoloArchivo = tarjeta.maximo === 1;
  // Fase 4 · fecha de emisión: la piden la tarjeta (sin elegir tipo) o el tipo elegido de cada archivo.
  const tiposConVigencia = new Set(tarjeta.tiposConVigencia ?? []);
  const pideFecha = (tipo: string | null) => (tipo ? tiposConVigencia.has(tipo) : false);
  const [fechaEmision, setFechaEmision] = useState("");
  const faltaFechaDeTarjeta = !eligeTipo && !!tarjeta.manejaVigencia && !fechaEmision;
  const [porClasificar, setPorClasificar] = useState<ArchivoPorClasificar[]>([]);

  const limites = useLimitesDocumentos();
  const [rechazados, setRechazados] = useState<{ nombre: string; motivo: string }[]>([]);
  const [preparando, setPreparando] = useState(false);

  const handleFiles = async (files: FileList) => {
    const todos = Array.from(files);
    if (!eligeTipo) {
      const total = unSoloArchivo ? Math.min(todos.length, 1) : todos.length;
      for (let i = 0; i < total; i++)
        subir.mutate({
          numeroCfdi, categoria: tarjeta.categoria, file: todos[i], tipoDocumento: tarjeta.tipoDocumento ?? undefined,
          fechaEmision: tarjeta.manejaVigencia ? fechaEmision : undefined,
        });
      return;
    }

    const esZip = (f: File) => aceptaZip && f.name.toLowerCase().endsWith(".zip");
    const sueltos = todos.filter((f) => !esZip(f));
    setPorClasificar((prev) => [
      ...prev,
      ...sueltos.map((file) => ({ id: crypto.randomUUID(), nombre: file.name, file, tipo: null as string | null, fechaEmision: null, subiendo: false })),
    ]);

    const zips = todos.filter(esZip);
    if (zips.length === 0) return;
    setPreparando(true);
    setRechazados([]);
    try {
      for (const zip of zips) {
        try {
          const r = await documentosApi.extraerZipAdicional(solicitudId, zip);
          setPorClasificar((prev) => [
            ...prev,
            ...r.archivos.map((a) => ({
              id: a.id, nombre: a.nombre, deZip: { loteId: r.loteId, archivoId: a.id }, tipo: null as string | null, fechaEmision: null, subiendo: false,
            })),
          ]);
          setRechazados((prev) => [...prev, ...r.rechazados.map((x) => ({ nombre: `${zip.name} › ${x.nombre}`, motivo: x.motivo }))]);
        } catch (e) {
          toastErrorApi(e);
        }
      }
    } finally {
      setPreparando(false);
    }
  };

  const actualizar = (id: string, patch: Partial<ArchivoPorClasificar>) => setPorClasificar((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const subirClasificado = async (archivo: ArchivoPorClasificar) => {
    if (!archivo.tipo || (pideFecha(archivo.tipo) && !archivo.fechaEmision)) return;
    actualizar(archivo.id, { subiendo: true });
    try {
      await subir.mutateAsync({
        numeroCfdi,
        categoria: tarjeta.categoria,
        file: archivo.file,
        deZip: archivo.deZip ? { ...archivo.deZip, nombre: archivo.nombre } : undefined,
        tipoDocumento: archivo.tipo,
        fechaEmision: pideFecha(archivo.tipo) ? archivo.fechaEmision ?? undefined : undefined,
      });
      setPorClasificar((prev) => prev.filter((a) => a.id !== archivo.id));
    } catch {
      // El hook ya notificó el error · el archivo se queda para reintentar.
      actualizar(archivo.id, { subiendo: false });
    }
  };

  const hayContenido = documentos.length > 0;
  const obligatorio = tarjeta.minimo > 0;
  const estadoDropzone: CategoriaDropzoneEstado = tarjeta.cumple && hayContenido ? "cargado" : obligatorio ? "vacio-requerido" : "vacio-opcional";
  const puedeSubirMas = documentos.length < (tarjeta.maximo ?? Number.POSITIVE_INFINITY);

  return (
    <Card
      padding="lg"
      style={{
        borderLeft: tarjeta.cumple && hayContenido ? "3px solid var(--color-multiva-400)" : !tarjeta.cumple ? "3px solid var(--color-advertencia-300)" : undefined,
      }}
    >
      <Stack gap="md">
        <div>
          <Group gap={8} align="center">
            {tarjeta.cumple && hayContenido ? (
              <ThemeIcon size="sm" variant="filled" color="multiva">
                <IconCheck size={12} stroke={2.5} />
              </ThemeIcon>
            ) : !tarjeta.cumple ? (
              <ThemeIcon size="sm" variant="filled" color="advertencia">
                <IconAlertTriangle size={12} stroke={2} />
              </ThemeIcon>
            ) : null}
            <Text fw={600} size="sm">
              {etiqueta}
              {tarjeta.porCfdi && tarjeta.numeroCfdi ? ` · CFDI ${tarjeta.numeroCfdi}` : ""}
            </Text>
            {tarjeta.cumple ? (
              <Badge variant="light" color={hayContenido ? "multiva" : "slate"}>
                {hayContenido ? "Completo" : "Opcional"}
              </Badge>
            ) : (
              <Badge variant="light" color="advertencia">
                {tarjeta.maximo !== null && tarjeta.cargados > tarjeta.maximo ? "Excede el máximo" : "Falta · obligatorio"}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" className="mt-1">
            {[tarjeta.categoriaDescripcion, `${textoCantidad(tarjeta)} · ${tarjeta.extensiones.join(", ")}${aceptaZip ? " · o un .zip con ellos" : ""}`]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </div>

        {puedeAdjuntar && tarjeta.puedeAgregar && puedeSubirMas && !eligeTipo && tarjeta.manejaVigencia && (
          <DatePickerInput
            label="Fecha de emisión"
            description="Este documento tiene vigencia · se calcula a partir de su fecha de emisión."
            required
            size="xs"
            wrapperClassName="max-w-60"
            maxDate={hoyNegocio()}
            value={parseIso(fechaEmision)}
            onChange={(d) => setFechaEmision(formatIso(d))}
          />
        )}

        {puedeAdjuntar && tarjeta.puedeAgregar && puedeSubirMas && !faltaFechaDeTarjeta && (
          <CategoriaDropzone
            accept={accept}
            maxMb={limites.tamanoMaximoMb}
            cargando={subir.isPending || preparando}
            estado={estadoDropzone}
            onFiles={(files) => void handleFiles(files)}
            single={unSoloArchivo}
          />
        )}

        {rechazados.length > 0 && (
          <Alert variant="warning" title={`${rechazados.length} archivo(s) no se pueden subir`}>
            <Stack gap={2}>
              {rechazados.map((r, i) => (
                <Text key={`${r.nombre}-${i}`} size="xs">
                  <span className="font-medium">{r.nombre}</span> · {r.motivo}
                </Text>
              ))}
              <div>
                <Button size="xs" variant="subtle" onClick={() => setRechazados([])}>
                  Entendido
                </Button>
              </div>
            </Stack>
          </Alert>
        )}

        {porClasificar.length > 0 && (
          <Stack gap={6}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Clasifica cada documento para subirlo
            </Text>
            {porClasificar.map((a) => (
              <Group key={a.id} gap="sm" wrap="nowrap" className="rounded-lg border border-border p-2">
                <IconFile size={18} stroke={1.6} className="shrink-0" />
                <Text size="sm" truncate title={a.nombre} className="min-w-0 flex-1">
                  {a.nombre}
                </Text>
                <Select
                  placeholder="Tipo de documento…"
                  searchable
                  options={tarjeta.tiposPermitidos.map((t) => ({ value: t, label: t }))}
                  value={a.tipo ?? undefined}
                  onValueChange={(v) => actualizar(a.id, { tipo: v })}
                  disabled={a.subiendo}
                  size="xs"
                  className="w-60"
                />
                {pideFecha(a.tipo) && (
                  <DatePickerInput
                    placeholder="Fecha de emisión"
                    size="xs"
                    wrapperClassName="w-40"
                    maxDate={hoyNegocio()}
                    disabled={a.subiendo}
                    value={parseIso(a.fechaEmision ?? "")}
                    onChange={(d) => actualizar(a.id, { fechaEmision: formatIso(d) || null })}
                  />
                )}
                <Button
                  size="xs"
                  leftSection={<IconUpload size={14} stroke={1.7} />}
                  disabled={!a.tipo || (pideFecha(a.tipo) && !a.fechaEmision)}
                  loading={a.subiendo}
                  onClick={() => void subirClasificado(a)}
                >
                  Subir
                </Button>
                <IconAction
                  label={`Quitar ${a.nombre}`}
                  variant="subtle"
                  size="sm"
                  disabled={a.subiendo}
                  onClick={() => {
                    setPorClasificar((prev) => prev.filter((x) => x.id !== a.id));
                    if (a.deZip) void documentosApi.descartarArchivoDeZip(solicitudId, a.deZip.loteId, a.deZip.archivoId).catch(() => undefined);
                  }}
                >
                  <IconX size={16} stroke={1.7} />
                </IconAction>
              </Group>
            ))}
          </Stack>
        )}

        {hayContenido ? (
          <Stack gap={6}>
            {documentos.map((d) => (
              <DocumentoRow key={d.id} documento={d} solicitudId={solicitudId} puedeEliminar={puedeAdjuntar} puedeReemplazar={puedeAdjuntar} revision={revisiones?.get(d.documentoId)} />
            ))}
          </Stack>
        ) : !puedeAdjuntar ? (
          <Text size="xs" c="dimmed">
            Sin documentos para este requisito.
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
